import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Dialog, Slide } from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import confirmationEmailResend from 'network/confirmationEmailResend';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import TextInput from 'components/input/TextInput';

const useStyles = makeStyles(() => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const StyledDialog = withStyles(() => ({
  paper: {
    borderRadius: '8px',
  },
  paperFullScreen: {
    borderRadius: '8px 8px 0 0',
  },
}))(Dialog);

const ModalContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  padding: 17px 16px 24px;
  display: flex;
  flex-direction: column;

  @media only screen and (min-width: 960px) {
    height: auto;
    max-width: 400px;
    max-height: 570px;
    padding: 16px 28px;
    align-items: flex-end;
  }
`;

const StyledSnackbar = withStyles(() => ({
  root: {
    position: 'absolute',
    width: '100%',
  },
}))(Snackbar);

const StyledAlert = withStyles(() => ({
  root: {
    width: '100%',
    margin: '34px 16px',
    '@media only screen and (min-width: 960px)': {
      margin: '34px 28px',
    },
  },
  filledSuccess: {
    background: '#F2F9EC',
    border: '1px solid #7EBF50',
    borderRadius: '4px',
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.3px',
    color: '#7EBF50',
  },
}))(Alert);

const ResendButton = styled.button`
  width: 100%;
  border-radius: 4px;
  padding: 12px;
  font-size: 18px;
  line-height: 18px;
  outline: none;
  color: #FFF;
  border: none;
  background-color: #282B40;
  transition: opacity 400ms;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const StyledTitle = styled.h2`
  text-align: center;
  margin-bottom: 20px;
`;

const StyledCloseIcon = styled(CloseIcon)`
  font-size: 28px;
  color: #A2AAB3;
  cursor: pointer;

  @media only screen and (min-width: 960px) {
    margin-right: -10px;
  }
`;

const ReminderText = styled.div`
  font-weight: 900;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  color: #E2654D;
  margin: 24px 36px 26px;
`;

const DescriptionText = styled.div`
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  color: #282B40;
  margin: 16px 0;
`;

const EmailInput = styled(TextInput)`
  padding: 0;
  margin: 11px 0 24px;

  input {
    font-size: 16px;
  }
`;

const ClickableText = styled.span`
  color: #5C8DF3;
  cursor: pointer;
  margin: 0 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const RegisterStickyContainer = styled.div`
  text-align: center;
  padding: 0 3px;

  @media only screen and (min-width: 900px) {
    padding-top: 80px;
  }
`;

const ContactSupportContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  padding: 0 15px;
  grid-gap: 5px;
  margin: 24px 0;
  white-space: break-spaces;
`;

const StyledHelpOutlineIcon = withStyles(() => ({
  root: {
    marginRight: '5.5px',
  },
}))(HelpOutlineIcon);

const ContactSupportText = styled.span`
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  color: #5C8DF3;
`;

const PageSplitter = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: auto;
`;

const MessageOfResetContainer = styled.div`
  margin-top: 5px;
  color: red;
  font-style: italic;
  position: relative;
  top: 5px;
  text-align: center;
`;

const ModalTransition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} mountOnEnter unmountOnExit {...props} />
));

const ResetEmailModal = ({ open, handleClose, onRegisterNowBtnClick }) => {
  const classes = useStyles();
  const [email, setEmail] = useState('');
  const [isResent, setIsResent] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [messageOfResent, setMessageOfResent] = useState('');
  const isMobile = useMediaQuery('(max-width: 960px)');

  const onEmailChange = useCallback(({ value }) => {
    setEmail(value);
  }, [setEmail]);

  const onModalExited = () => {
    setEmail('');
    setIsResent(false);
    setIsResending(false);
    setMessageOfResent(false);
  };

  const onResendClick = useCallback(async () => {
    setIsResending(true);
    const resendResult = await confirmationEmailResend(email.trim());
    if (resendResult.success) {
      setIsResent(true);
      setMessageOfResent('');
    } else {
      setMessageOfResent('Our server is currently busy, please try again later.');
    }
    setIsResending(false);
  }, [setIsResending, setIsResent, setMessageOfResent, email]);

  const onEmailInputKeyPress = useCallback((event) => {
    if (event.keyCode === 13) {
      onResendClick();
    }
  }, [onResendClick]);

  const onCsLinkClick = () => {
    const { $zopim } = window;
    $zopim(() => {
      $zopim.livechat.window.show();
    });
  };

  return (
    <StyledDialog
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={classes.modal}
      open={open}
      onClose={handleClose}
      onExited={onModalExited}
      closeAfterTransition
      BackdropProps={{
        timeout: 500,
      }}
      TransitionComponent={ModalTransition}
      fullScreen={isMobile}
    >
      <ModalContainer>
        <StyledCloseIcon onClick={handleClose} />
        <StyledSnackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={isResent}
          onClose={() => setIsResent(false)}
          autoHideDuration={3000}
        >
          <StyledAlert
            severity="success"
            variant="filled"
            iconMapping={{ success: <CheckCircleIcon /> }}
          >
            Email Sent
          </StyledAlert>
        </StyledSnackbar>
        <PageSplitter>
          <div>
            <StyledTitle id="transition-modal-title">
              Resend the access link
            </StyledTitle>
            <ReminderText>
              Only registered and approved users can receive the email.
            </ReminderText>
            <EmailInput
              label="Registered Email"
              placeholder="Enter email"
              onChange={onEmailChange}
              onKeyPress={onEmailInputKeyPress}
            />
            {messageOfResent && (
              <MessageOfResetContainer>
                {messageOfResent}
              </MessageOfResetContainer>
            )}
            <ResendButton
              onClick={onResendClick}
              disabled={isResending || !email || email.trim() === ''}
            >
              {
                isResending ? (
                  'Resending'
                ) : (
                  'Resend'
                )
              }
            </ResendButton>
            <DescriptionText>
              The email will be sent within 10 mins, if there are no emails received, please try again.
            </DescriptionText>
            <ContactSupportContainer>
              <StyledHelpOutlineIcon />
              <ClickableText
                onClick={onCsLinkClick}
              >
                <ContactSupportText>
                  Contact customer support
                </ContactSupportText>
              </ClickableText>
            </ContactSupportContainer>
          </div>

          <RegisterStickyContainer>
            New user?
            <ClickableText
              onClick={onRegisterNowBtnClick}
            >
              {' Register now.'}
            </ClickableText>
          </RegisterStickyContainer>
        </PageSplitter>
      </ModalContainer>
    </StyledDialog>
  );
};

ResetEmailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onRegisterNowBtnClick: PropTypes.func.isRequired,
};

export default ResetEmailModal;
